import { useState } from 'react';
import { Button, Spinner } from '@theraply/ui';
import {
  useAvailabilitySlots,
  useCreateSlot,
  useUpdateSlot,
  useDeleteSlot,
  useAvailabilityExceptions,
  useCreateException,
  useDeleteException,
  AvailabilitySlot,
} from '@theraply/api-client';
import WeeklySchedule from '../../components/availability/WeeklySchedule';
import SlotEditor from '../../components/availability/SlotEditor';
import ExceptionManager from '../../components/availability/ExceptionManager';

export default function AvailabilityPage() {
  const [slotEditorOpen, setSlotEditorOpen] = useState(false);
  const [exceptionManagerOpen, setExceptionManagerOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);

  // Availability Slots
  const { data: slots = [], isLoading: slotsLoading } = useAvailabilitySlots();
  const createSlotMutation = useCreateSlot();
  const updateSlotMutation = useUpdateSlot();
  const deleteSlotMutation = useDeleteSlot();

  // Availability Exceptions
  const { data: exceptions = [], isLoading: exceptionsLoading } = useAvailabilityExceptions();
  const createExceptionMutation = useCreateException();
  const deleteExceptionMutation = useDeleteException();

  const handleOpenSlotEditor = (slot?: AvailabilitySlot) => {
    setSelectedSlot(slot || null);
    setSlotEditorOpen(true);
  };

  const handleCloseSlotEditor = () => {
    setSlotEditorOpen(false);
    setSelectedSlot(null);
  };

  const handleSaveSlot = async (data: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    sessionType: string;
    bufferMinutes: number;
  }) => {
    try {
      if (selectedSlot) {
        await updateSlotMutation.mutateAsync({
          id: selectedSlot.id,
          ...data,
        });
      } else {
        await createSlotMutation.mutateAsync(data);
      }
      handleCloseSlotEditor();
    } catch (error) {
      console.error('Error saving slot:', error);
    }
  };

  const handleDeleteSlot = async (id: number) => {
    try {
      await deleteSlotMutation.mutateAsync(id);
      handleCloseSlotEditor();
    } catch (error) {
      console.error('Error deleting slot:', error);
    }
  };

  const handleAddException = async (data: {
    date: string;
    allDay: boolean;
    startTime?: string;
    endTime?: string;
    reason?: string;
  }) => {
    try {
      await createExceptionMutation.mutateAsync(data);
    } catch (error) {
      console.error('Error adding exception:', error);
    }
  };

  const handleDeleteException = async (id: number) => {
    try {
      await deleteExceptionMutation.mutateAsync(id);
    } catch (error) {
      console.error('Error deleting exception:', error);
    }
  };

  const isLoading =
    slotsLoading ||
    exceptionsLoading ||
    createSlotMutation.isPending ||
    updateSlotMutation.isPending ||
    deleteSlotMutation.isPending ||
    createExceptionMutation.isPending ||
    deleteExceptionMutation.isPending;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Availability Management</h1>
        <p className="mt-2 text-sm text-gray-600">
          Set your weekly schedule and manage time off
        </p>
      </div>

      {/* Controls */}
      <div className="mb-6 flex gap-3">
        <Button
          onClick={() => handleOpenSlotEditor()}
          disabled={isLoading}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          {isLoading && <Spinner />}
          Add Time Slot
        </Button>
        <Button
          onClick={() => setExceptionManagerOpen(true)}
          disabled={isLoading}
          className="bg-secondary-600 hover:bg-secondary-700 text-white"
        >
          Manage Time Off
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Available Hours</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            {slots.reduce((total, slot) => {
              const [startH, startM] = slot.startTime.split(':').map(Number);
              const [endH, endM] = slot.endTime.split(':').map(Number);
              return total + (endH - startH) + (endM - startM) / 60;
            }, 0).toFixed(1)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Weekly Slots</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{slots.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Time Off Days</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{exceptions.length}</p>
        </div>
      </div>

      {/* Weekly Schedule */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Weekly Schedule</h2>
        {slotsLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <WeeklySchedule
            slots={slots}
            onSlotClick={handleOpenSlotEditor}
            onEmptySlotClick={(dayOfWeek, startTime) => {
              setSelectedSlot(null);
              setSlotEditorOpen(true);
            }}
          />
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2">How to use</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>- Click any hour block to create a new availability slot</li>
          <li>- Click an existing slot to edit or delete it</li>
          <li>- Use "Manage Time Off" to block entire days or specific times</li>
          <li>- Your availability will be visible to clients for booking</li>
        </ul>
      </div>

      {/* Modals */}
      <SlotEditor
        slot={selectedSlot}
        isOpen={slotEditorOpen}
        isLoading={
          createSlotMutation.isPending ||
          updateSlotMutation.isPending ||
          deleteSlotMutation.isPending
        }
        onSave={handleSaveSlot}
        onDelete={selectedSlot ? handleDeleteSlot : undefined}
        onClose={handleCloseSlotEditor}
      />

      <ExceptionManager
        exceptions={exceptions}
        isLoading={createExceptionMutation.isPending || deleteExceptionMutation.isPending}
        isOpen={exceptionManagerOpen}
        onAddException={handleAddException}
        onDeleteException={handleDeleteException}
        onClose={() => setExceptionManagerOpen(false)}
      />
    </div>
  );
}
